import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { GATEWAY_IMAGES } from "@/data/content";

const Panel = ({ innerRef, img, alt, label, sub, testId, onEnter, onLeave, onClick, top }) => (
  <button
    ref={innerRef}
    data-testid={testId}
    onMouseEnter={onEnter}
    onMouseLeave={onLeave}
    onFocus={onEnter}
    onBlur={onLeave}
    onClick={onClick}
    style={{ flexGrow: 1, flexBasis: 0 }}
    className="gw-panel group relative min-h-0 overflow-hidden text-left"
    aria-label={`Enter the ${label} portal`}
  >
    <img
      src={img}
      alt={alt}
      className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
    />
    <div className="absolute inset-0 bg-[#0B0F19]/70 transition-colors duration-700 group-hover:bg-[#0B0F19]/45" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-transparent to-[#0B0F19]/60" />
    <div
      className={`relative z-10 flex h-full flex-col items-center gap-3 px-6 text-center md:justify-end md:gap-4 md:pb-[12vh] ${
        top ? "justify-start pt-[7dvh]" : "justify-end pb-[7dvh]"
      }`}
    >
      <span className="hidden font-tech text-[10px] uppercase tracking-[0.4em] text-slate-400 opacity-0 md:block [@media(max-height:620px)]:hidden transition-opacity duration-500 group-hover:opacity-100">
        Enter Portal
      </span>
      <span className="font-heading text-2xl font-bold tracking-[0.18em] min-[380px]:text-3xl text-white transition-colors duration-500 group-hover:text-brand sm:text-4xl lg:text-5xl">
        {label}
      </span>
      <span className="hidden max-w-[240px] text-xs leading-relaxed text-slate-400 opacity-0 md:block [@media(max-height:620px)]:hidden transition-all duration-500 group-hover:opacity-100 sm:text-sm">
        {sub}
      </span>
      <span className="flex h-11 w-11 md:mt-2 md:h-12 md:w-12 items-center justify-center rounded-full border border-slate-600 text-slate-300 transition-all duration-500 group-hover:border-brand group-hover:bg-brand group-hover:text-white">
        <ArrowUpRight size={20} />
      </span>
    </div>
  </button>
);

export default function GatewayView({ onSelect }) {
  const root = useRef(null);
  const com = useRef(null);
  const res = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".gw-panel",
        { opacity: 0, scale: 1.04 },
        { opacity: 1, scale: 1, duration: 0.9, ease: "power3.out", stagger: 0.1 }
      );
    }, root);
    return () => ctx.revert();
  }, []);

  const [active, setActive] = useState(null);

  const expand = (target, other, which) => {
    setActive(which);
    gsap.to(target.current, { flexGrow: 1.75, duration: 0.8, ease: "power3.inOut" });
    gsap.to(other.current, { flexGrow: 0.5, duration: 0.8, ease: "power3.inOut" });
  };
  const reset = () => {
    setActive(null);
    gsap.to([com.current, res.current], { flexGrow: 1, duration: 0.8, ease: "power3.inOut" });
  };

  return (
    <main ref={root} className="relative h-[100dvh] min-h-[560px] w-full overflow-hidden" data-testid="gateway-view">
      <div className="flex h-full w-full flex-col md:flex-row [&>.gw-panel]:basis-0">
        <Panel
          innerRef={com}
          img={GATEWAY_IMAGES.commercial}
          alt="Modern corporate skyscraper glass facade"
          label="COMMERCIAL"
          sub="Hospitals · Hotels · Universities · Offices · Industry & More"
          testId="gateway-split-commercial"
          onEnter={() => expand(com, res, "commercial")}
          onLeave={reset}
          onClick={() => onSelect("commercial")}
          top
        />
        <div className="hidden w-px bg-slate-700/60 md:block" />
        <div className="h-px bg-slate-700/60 md:hidden" />
        <Panel
          innerRef={res}
          img={GATEWAY_IMAGES.residential}
          alt="Luxury smart home exterior at dusk"
          label="RESIDENTIAL"
          sub="Flats · Villas · Bungalows · Penthouses"
          testId="gateway-split-residential"
          onEnter={() => expand(res, com, "residential")}
          onLeave={reset}
          onClick={() => onSelect("residential")}
        />
      </div>

      <header
        className={`gw-brand pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center px-6 text-center transition-all md:justify-start md:pt-[16vh] [@media(max-height:620px)]:md:pt-[10vh] duration-500 ${
          active ? "-translate-y-10 opacity-0" : "translate-y-0 opacity-100"
        }`}
      >
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5 }}
          className="mb-4 font-tech text-[10px] uppercase tracking-[0.35em] sm:mb-5 sm:tracking-[0.5em] text-slate-400 sm:text-xs"
        >
          Electronic Infrastructure
        </motion.p>
        <h1 className="font-heading text-[2rem] font-extrabold leading-tight tracking-tight text-white drop-shadow-2xl min-[380px]:text-4xl sm:text-6xl lg:text-7xl">
          <span className="block overflow-hidden pb-2">
            <motion.span
              initial={{ y: "112%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1.1, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="block"
            >
              BIGBOSS <span className="text-brand">DIGITECH</span>
            </motion.span>
          </span>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.2 }}
          className="mt-4 font-tech text-[11px] tracking-[0.25em] text-slate-300 sm:mt-5 sm:text-sm sm:tracking-[0.35em]"
          data-testid="gateway-tagline"
        >
          secure <span className="text-brand">|</span> connect <span className="text-brand">|</span> automate
        </motion.p>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 1.5 }}
          className="mt-8 hidden text-xs text-slate-500 md:block [@media(max-height:620px)]:hidden"
        >
          Choose your division to continue
        </motion.p>
      </header>

      <div
        className={`pointer-events-none absolute inset-x-0 top-0 z-20 flex justify-center px-4 pt-6 text-center transition-all sm:pt-8 duration-500 ${
          active ? "translate-y-0 opacity-100" : "-translate-y-6 opacity-0"
        }`}
      >
        <p className="font-tech text-[11px] tracking-[0.3em] text-slate-300 sm:tracking-[0.4em]" data-testid="gateway-compact-brand">
          BIGBOSS <span className="text-brand">DIGITECH</span>
          <span className="mx-3 text-slate-600">·</span>
          <span className="hidden sm:inline">secure | connect | automate</span>
        </p>
      </div>
    </main>
  );
}
